import { invoke } from '@tauri-apps/api/core';
import type { PlaybackState } from '@streamit/contracts';
export type NativeError = { code: string; message: string };
// `auto` is retained only for snapshots from older installed receivers.
export type DesktopPlayerChoice = 'slc' | 'mpv' | 'vlc' | 'auto';
export type DesktopEngineChoice = 'auto' | 'mpv' | 'vlc';
export type DesktopPlayerPreferences = { defaultPlayer: DesktopPlayerChoice; slcEngine?: DesktopEngineChoice; recoverSelected?: boolean };
export type DesktopLifecycleView = { preferences: { pauseOnMinimize: boolean }; error: NativeError | null };
export type Pairing = {
  id: string;
  code: string;
  verificationUrl: string;
  expiresAt: number;
  status: string;
  kind: string;
};
export type NativeSnapshot = {
  paired: boolean;
  connection: string;
  infoPending?: boolean;
  infoEpoch?: number;
  info: null | {
    receiverRevision?: number;
    device: {
      id: string;
      name: string;
      householdId: string;
      profileId: string;
      providerConnectionId?: string;
    };
    profile: import('@streamit/contracts').Profile;
    household: { id: string; name: string };
    controllers: { userId: string; alias: string; createdAt: number }[];
  };
  pairing: Pairing | null;
  player: {
    playback: (PlaybackState & { subtitleDelay?: number }) | null;
    loading: boolean;
    localPlayback?: boolean;
    localFiles?: boolean;
    localSubtitles?: boolean;
    hasLocalSubtitle?: boolean;
    error: NativeError | null;
    preferences: DesktopPlayerPreferences;
    sessionChoice: DesktopPlayerChoice | null;
    sessionEngine?: DesktopEngineChoice | null;
    settingsError: NativeError | null;
    availablePlayers?: DesktopPlayerChoice[];
    completed?: { sessionId: string; mediaId: string; videoId: string; type: string; title: string };
  };
  error: NativeError | null;
  navigationId: number;
  navigation?: string;
  nextId?: number;
  foreground?: boolean;
  desktopLifecycle?: DesktopLifecycleView | null;
};
export const nativeMode = import.meta.env.MODE === 'desktop';
export async function native<T>(
  command: string,
  args?: Record<string, unknown>,
): Promise<T> {
  try {
    return await invoke<T>(command, args);
  } catch (error) {
    const e = error as Partial<NativeError>;
    throw Object.assign(
      new Error(
        e.message || 'The receiving app could not complete this action.',
      ),
      { code: e.code || 'NATIVE_ERROR' },
    );
  }
}
